import { useCallback, useState, useRef, useLayoutEffect } from "react";
import { useVideoCalling, useVideoCallingSession } from "./useVideoCallingContext";
import { useVideoRoom } from "./useVideoRoom";
import { useLocalTracks } from "./useLocalTracks";
import type { VideoCallingSlice, VideoCallingSession } from "../types";

/**
 * Hook for meeting lifecycle API operations.
 *
 * Handles starting, joining, and ending meetings via the Tellescope SDK.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { startMeeting, joinMeeting, endMeeting, leaveMeeting, isStarting, isJoining } = useMeetingOperations();
 *
 *   return (
 *     <div>
 *       <button onClick={() => startMeeting(calendarEventId)} disabled={isStarting}>
 *         {isStarting ? 'Starting...' : 'Start Meeting'}
 *       </button>
 *       <button onClick={() => joinMeeting(calendarEventId)} disabled={isJoining}>
 *         {isJoining ? 'Joining...' : 'Join Meeting'}
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useMeetingOperations() {
  const videoCalling = useVideoCalling();
  const session = useVideoCallingSession();
  const { connectToRoom, disconnectFromRoom } = useVideoRoom();
  const { createLocalTracks, destroyLocalTracks } = useLocalTracks();

  // Use refs to access latest values without needing them as callback dependencies
  // This prevents callbacks from being recreated on every state change
  const videoCallingRef = useRef<VideoCallingSlice>(videoCalling);
  const sessionRef = useRef<VideoCallingSession | null>(session);
  useLayoutEffect(() => {
    videoCallingRef.current = videoCalling;
    sessionRef.current = session;
  });

  // Loading states
  const [isStarting, setIsStarting] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isEnding, setIsEnding] = useState(false);

  /**
   * Start a meeting (for hosts).
   *
   * Creates the Twilio room, gets the access token, and connects.
   *
   * @param calendarEventId - ID of the calendar event
   * @returns The meeting ID
   */
  const startMeeting = useCallback(
    async (calendarEventId: string): Promise<string> => {
      const currentSession = sessionRef.current;
      if (!currentSession) {
        throw new Error("No session available");
      }

      setIsStarting(true);

      try {
        // Create local tracks for preview
        await createLocalTracks();

        // Call backend to start meeting
        const response = await currentSession.api.meetings.start_meeting_for_event({
          calendarEventId,
        });

        const token = response.host.info.JoinToken;
        const roomName = response.meeting.Meeting.ExternalMeetingId;

        // Update meeting metadata
        videoCallingRef.current.setMeetingMeta({
          meetingId: response.id,
          calendarEventId,
          roomName,
          isHost: true,
        });

        // Connect to the room
        await connectToRoom(token, roomName);

        // Exit waiting room
        videoCallingRef.current.setIsWaitingRoom(false);

        return response.id;
      } catch (error) {
        console.error("Failed to start meeting:", error);
        // Clean up on error
        destroyLocalTracks();
        throw error;
      } finally {
        setIsStarting(false);
      }
    },
    [createLocalTracks, connectToRoom, destroyLocalTracks]
  );

  /**
   * Join an existing meeting (for participants).
   *
   * Gets the access token and connects to the room.
   *
   * @param calendarEventId - ID of the calendar event
   */
  const joinMeeting = useCallback(
    async (calendarEventId: string): Promise<void> => {
      const currentSession = sessionRef.current;
      if (!currentSession) {
        throw new Error("No session available");
      }

      setIsJoining(true);

      try {
        // Create local tracks for preview
        await createLocalTracks();

        // Call backend to join meeting
        const response = await currentSession.api.meetings.join_meeting_for_event({
          calendarEventId,
        });

        const token = response.attendee.info.JoinToken;
        const roomName = response.meeting.Meeting.ExternalMeetingId;

        // Update meeting metadata
        videoCallingRef.current.setMeetingMeta({
          meetingId: response.id,
          calendarEventId,
          roomName,
          isHost: false,
        });

        // Connect to the room
        await connectToRoom(token, roomName);

        // Exit waiting room
        videoCallingRef.current.setIsWaitingRoom(false);
      } catch (error) {
        console.error("Failed to join meeting:", error);
        // Clean up on error
        destroyLocalTracks();
        throw error;
      } finally {
        setIsJoining(false);
      }
    },
    [createLocalTracks, connectToRoom, destroyLocalTracks]
  );

  /**
   * End the meeting for all participants (host only).
   */
  const endMeeting = useCallback(async (): Promise<void> => {
    const currentSession = sessionRef.current;
    if (!currentSession) {
      throw new Error("No session available");
    }

    const meetingId = videoCallingRef.current.meetingMeta.meetingId;
    if (!meetingId) {
      throw new Error("No meeting ID available");
    }

    setIsEnding(true);

    try {
      // Call backend to end meeting
      await currentSession.api.meetings.end_meeting({ id: meetingId });

      // Disconnect from room
      disconnectFromRoom();

      // Destroy local tracks
      destroyLocalTracks();

      // Reset video state
      videoCallingRef.current.resetVideoState();

      // Hide the window
      videoCallingRef.current.setIsVisible(false);
    } catch (error) {
      console.error("Failed to end meeting:", error);
      throw error;
    } finally {
      setIsEnding(false);
    }
  }, [disconnectFromRoom, destroyLocalTracks]);

  /**
   * Leave the meeting (for participants, or host without ending for all).
   */
  const leaveMeeting = useCallback(() => {
    // Disconnect from room
    disconnectFromRoom();

    // Destroy local tracks
    destroyLocalTracks();

    // Reset video state
    videoCallingRef.current.resetVideoState();

    // Hide the window
    videoCallingRef.current.setIsVisible(false);
  }, [disconnectFromRoom, destroyLocalTracks]);

  return {
    // Loading states
    isStarting,
    isJoining,
    isEnding,

    // Actions
    startMeeting,
    joinMeeting,
    endMeeting,
    leaveMeeting,
  };
}
